import { type CardTabListType } from 'antd/lib/card';
import i18n from 'i18next';

import { getTableColumnFilterValues } from 'components/data/helpers/tableFilterUtils';
import { type PresetFilterType } from 'components/DataTable';
import { tableStorage } from 'components/DataTable/utils';
import {
  type AtlasGql_DamageSchemaAttributeSelection,
  AtlasGqlConfirmationStatus,
} from 'types/atlas-graphql';

export const SITE_DAMAGE_LIST_TABLE_ID = 'site-damage-list-table2';
export const DAMAGE_LIST_TABLE_ID = 'damage-list-table2';

export const isSiteDamageListTable = (id: string) => id === SITE_DAMAGE_LIST_TABLE_ID;

export type DamageSchemaFilterColumn = {
  key?: string | number;
  type?: string;
  config?: Record<string, unknown>;
};

type GetDamageSchemaFilterStateParams = {
  columns: DamageSchemaFilterColumn[];
  currentColumnKey: string;
  currentSelectedKeys: unknown[];
  filteredInfo?: Record<string, unknown>;
};

type DamageSchemaFilterState = {
  hasMultipleSelections: boolean;
  selections: AtlasGql_DamageSchemaAttributeSelection[];
};

type GetDamageSchemaPrefetchSelectionsParams = {
  columns: DamageSchemaFilterColumn[];
  filteredInfo?: Record<string, unknown>;
};

const getSelectFilterValues = (
  filteredInfo: Record<string, unknown> | undefined,
  columnKey: string
): string[] => getTableColumnFilterValues(filteredInfo, columnKey);

const normalizeDamageSchemaStringValue = (value: string): string => (value === ' ' ? '' : value);

export const getSelectedDamageSchemaFilterValues = (selectedKeys: unknown[]): string[] =>
  getSelectFilterValues({ selected: selectedKeys }, 'selected').map(
    normalizeDamageSchemaStringValue
  );

export const getDamageSchemaFilterState = ({
  columns,
  currentColumnKey,
  currentSelectedKeys,
  filteredInfo,
}: GetDamageSchemaFilterStateParams): DamageSchemaFilterState => {
  const selectColumnKeys = Array.from(
    new Set(
      columns
        .filter(({ type, key }) => type === 'string' && typeof key === 'string')
        .map(({ key }) => key as string)
    )
  );

  const selections: AtlasGql_DamageSchemaAttributeSelection[] = [];

  for (const columnKey of selectColumnKeys) {
    const values =
      columnKey === currentColumnKey
        ? getSelectFilterValues({ [columnKey]: currentSelectedKeys }, columnKey)
        : getSelectFilterValues(filteredInfo, columnKey);

    if (values.length > 1) {
      return { hasMultipleSelections: true, selections: [] };
    }

    if (columnKey !== currentColumnKey && values.length === 1) {
      selections.push({
        name: columnKey,
        string: normalizeDamageSchemaStringValue(values[0]),
      });
    }
  }

  return { hasMultipleSelections: false, selections };
};

export const getDamageSchemaPrefetchSelections = ({
  columns,
  filteredInfo,
}: GetDamageSchemaPrefetchSelectionsParams): AtlasGql_DamageSchemaAttributeSelection[][] => {
  const uniqueSelections = new Map<string, AtlasGql_DamageSchemaAttributeSelection[]>();

  for (const column of columns) {
    if (column.type !== 'string' || typeof column.key !== 'string') {
      continue;
    }

    const currentSelectedKeys = Array.isArray(filteredInfo?.[column.key])
      ? filteredInfo[column.key]
      : [];
    const { hasMultipleSelections, selections } = getDamageSchemaFilterState({
      columns,
      currentColumnKey: column.key,
      currentSelectedKeys,
      filteredInfo,
    });

    if (!hasMultipleSelections) {
      uniqueSelections.set(JSON.stringify(selections), selections);
    }
  }

  return Array.from(uniqueSelections.values());
};

type GetConfirmedTabListParams = {
  critical?: boolean;
  isSiteDetailsPage: boolean;
  assetIds?: string[];
};

export const getConfirmedTabList = ({
  critical = false,
  isSiteDetailsPage,
  assetIds,
}: GetConfirmedTabListParams): (CardTabListType & PresetFilterType)[] => [
  {
    key: 'ALL',
    tab: i18n.t('table.all', 'All'),
    value: {
      filters: {
        confirmationStatus: undefined,
        site: isSiteDetailsPage ? assetIds : undefined,
      },
      sortOrder: {
        columnKey: critical ? 'Date' : 'Severity',
        order: 'descend',
      },
    },
  },
  {
    key: 'CONFIRMED',
    tab: i18n.t('table.confirmed', 'Confirmed'),
    value: {
      filters: {
        confirmationStatus: [AtlasGqlConfirmationStatus.Confirmed],
        site: isSiteDetailsPage ? assetIds : undefined,
      },
    },
  },
  {
    key: 'UNCONFIRMED',
    tab: i18n.t('table.unconfirmed', 'Unconfirmed'),
    value: {
      filters: {
        confirmationStatus: [
          AtlasGqlConfirmationStatus.NotConfirmed,
          AtlasGqlConfirmationStatus.ConfirmedUpdated,
        ],
        site: isSiteDetailsPage ? assetIds : undefined,
      },
    },
  },
];

export const getInitialPresetFilter = (
  params: GetConfirmedTabListParams & { defaultActiveTabKey?: string }
) => {
  const { defaultActiveTabKey = 'ALL', ...tabListParams } = params;
  return getConfirmedTabList(tabListParams).find(tab => tab.key === defaultActiveTabKey);
};

/**
 * Removes the persisted per-customer state (sort/filter/pagination) for the Inspections
 * Damage table 2.0 list, so table state does not leak between orgs when switching customers
 * or logging out.
 */
export const clearDamageListTable2 = () => {
  const { remove } = tableStorage({ storageKey: DAMAGE_LIST_TABLE_ID });
  remove();
};
